import {
	Button,
	ButtonKind,
	Tag
} from "client/Components";
import {
	generateTrackingId,
	isNil,
	setTrackingId
} from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	PropsWithChildren,
	ReactElement
} from "react";
import { UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import styles from "./UserLicenseCard.module.less";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";

export type UserLicenseCardStatus = "active" | "inactive";

export type UserLicenseCardProps = {
	trackingId: string;
};

export const UserLicenseCard = (props: PropsWithChildren<UserLicenseCardProps>): ReactElement => {
	const {
		trackingId,
		children
	} = props;

	return (
		<section
			{...setTrackingId(trackingId)}
			className={styles.root}
		>
			{children}
		</section>
	);
};
UserLicenseCard.displayName = "UserLicenseCard";

export type UserLicenseCardHeaderProps = {
	kind: UserLicense;
	status?: UserLicenseCardStatus;
};

const Header = (props: PropsWithChildren<UserLicenseCardHeaderProps>): ReactElement => {
	const {
		kind,
		status,
		children
	} = props;

	const renderStatusTag = (): ReactElement | null => {
		if (isNil(status)) {
			return null;
		}

		switch (status) {
			case "active":
				return (
					<Tag
						text={localize("_Active")}
						color="success"
						trackingId={generateTrackingId("status", `|${"active"}`)}
					/>
				);
			case "inactive":
				return (
					<Tag
						text={localize("_Inactive")}
						color="danger"
						trackingId={generateTrackingId("status", `|${"inactive"}`)}
					/>
				);
			default:
				never(status);
		}
	};

	return (
		<header
			{...setTrackingId("header")}
			className={styles.header}
		>
			<div className={styles.titleRow}>
				<h3
					{...setTrackingId("title")}
					className={styles.title}
				>
					{getUserLicenseName(kind)}
				</h3>
				{renderStatusTag()}
			</div>
			<div className={styles.headerContent}>
				{children}
			</div>
		</header>
	);
};
Header.displayName = "UserLicenseCard.Header";

const Content = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div
			{...setTrackingId("content")}
			className={styles.content}
		>
			{children}
		</div>
	);
};
Content.displayName = "UserLicenseCard.Content";

export type UserLicenseCardFooterButtonProps = {
	kind: ButtonKind;
	text: string;
	onClick?(): void;
	isDisabled?: boolean;
};

const FooterButton = (props: UserLicenseCardFooterButtonProps): ReactElement => {
	const {
		kind,
		text,
		onClick,
		isDisabled = false
	} = props;

	return (
		<footer
			{...setTrackingId("footer")}
			className={styles.footer}
		>
			<Button
				width="fill"
				kind={kind}
				text={text}
				onClick={onClick}
				isDisabled={isDisabled}
				trackingId="button"
				size="large"
			/>
		</footer>
	);
};
FooterButton.displayName = "UserLicenseCard.FooterButton";

UserLicenseCard.Header = Header;
UserLicenseCard.Content = Content;
UserLicenseCard.FooterButton = FooterButton;
